// ===================================================================================================
// InstantQuotesConfigCrud.jsx
// ---------------------------------------------------------------------------------------------------
// This screen opens when the user clicks "View" on a saved Instant Quote (a Rate Offer).
// It fetches the RateOffer by Id and renders a single tile with the exact same visual spec
// used in the search results (via the shared ResultsTile component).
// The goal is to provide a focused, read-only "view" experience for one saved quote.
//
// Extra goodies:
// - Per-tab persistence: each browser tab keeps its own loaded offer and selected line id.
// - Context isolation: opening multiple quotes across tabs won't leak state between them.
// - No runaway effects: effects are scoped and dependencies kept tight to avoid loops.
// - Shape normalization: convert backend PascalCase to the camelCase shape the tile expects,
//   mirroring the search-results normalization so values actually render.
// ===================================================================================================

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  BufferService,
  M_StateActions,
  MHeader,
} from "src/M_Components";
import ResultsTile from "../../components/InstantQuotesSearch/ResultsTile";

//==========================================
// helper to read a keyed value from the global M_State reducer snapshot
//==================================
const pickFromState = (arr, key) =>
  (arr || []).find((x) => x.key === key)?.value;

//==========================================
// helper to read a keyed value from a tab snapshot array
//==================================
const pickFromTabSnapshot = (arr, key) =>
  (arr || []).find((x) => x.key === key)?.value;

//==========================================
// pascalToCamel: tiny helper to turn "SomeKey" into "someKey"
//==================================
const pascalToCamel = (s) => (s ? s[0].toLowerCase() + s.slice(1) : s);

//==========================================
// normalizeDeep:
// Walk an object/array and rebuild it with camelCase keys,
// keeping the untouched original payload in "__raw" at the root.
// This mirrors the search-results normalization so the tile renders identically.
//==================================
const normalizeDeep = (node, isRoot = false) => {
  if (Array.isArray(node)) return node.map((n) => normalizeDeep(n, false));
  if (node && typeof node === "object") {
    const out = {};
    for (const k of Object.keys(node)) {
      const ck = pascalToCamel(k);
      out[ck] = normalizeDeep(node[k], false);
    }
    // stash original only on the root normalized offer, not on every nested object
    if (isRoot) out.__raw = node;
    return out;
  }
  return node;
};

export default function InstantQuotesConfigCrud({ toastMessages }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);

  //==========================================
  // per-tab identity
  // - we read selectedTab once, and keep our own tabId state
  // - when selectedTab changes, we restore that tab's snapshot
  //==================================
  const selectedTab = pickFromState(M_State, "selectedTab"); // current tab id in global shell
  const [tabId, setTabId] = useState(selectedTab); // local memo of "which tab am I"

  //==========================================
  // local state for the loaded RateOffer and the selected line id
  // - "lineId" is the Id we must fetch by
  // - "offer" holds the loaded (normalized) RateOffer payload
  //==================================
  const [lineId, setLineId] = useState(null);
  const [offer, setOffer] = useState(null);

  // keep a small flag so we only seed from globals once on mount
  const didSeedRef = useRef(false);

  //==========================================
  // a service instance bound to RateOffers API (stable while base URL stays the same)
  //==================================
  const svcOffers = useMemo(
    () => new BufferService("RateOffers", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  //==========================================
  // Boot sequence (runs once):
  // - Try restoring this tab's snapshot (offer + lineId).
  // - If none, seed from global "InstantQuoteLine" exactly once, then clear the seed (optional).
  // - If we end up with a lineId and no offer, fetch it.
  //==================================
  useEffect(() => {
    if (didSeedRef.current) return; // ensure we only run this seeding once
    didSeedRef.current = true;

    // attempt to restore a per-tab snapshot by current tabId
    const tabSnap = pickFromState(M_State, tabId);
    if (tabSnap && Array.isArray(tabSnap)) {
      const snapOffer = pickFromTabSnapshot(tabSnap, "offer");
      const snapLineId = pickFromTabSnapshot(tabSnap, "lineId");
      if (snapOffer) setOffer(snapOffer);
      if (snapLineId) setLineId(snapLineId);
    }

    // if no lineId yet, try global seed "InstantQuoteLine" (set by InstantQuotesConfig)
    if (!lineId) {
      const seedRow = pickFromState(M_State, "InstantQuoteLine");
      const seedId = seedRow?.Id || seedRow?.id || null; // accept either casing
      if (seedId != null) {
        setLineId(seedId);
        // optional: clear the seed so other tabs don't pick it up again
        dispatch(M_StateActions.removeM_State({ key: "InstantQuoteLine" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty deps: we seed only once on mount

  //==========================================
  // Fetch the RateOffer when we have a lineId and no offer loaded yet.
  // Normalize the response to camelCase so the shared Tile reads it correctly.
  // This effect is tight: it only depends on lineId and svcOffers base URL.
  //==================================
  useEffect(() => {
    if (!lineId || offer) return; // nothing to do

    let isCancelled = false;

    const run = async () => {
      try {
        ctx.setIsLoad(true);
        // BufferService.getId usually hits GET /RateOffers/{id}
        // If your backend is /api/RateOffers/Id?id={id}, map it inside BufferService.
        const res = await svcOffers.getId(lineId);

        // Normalize shape (PascalCase -> camelCase) and attach __raw for future POSTs
        const normalized = normalizeDeep(res || {}, true);

        if (!isCancelled) setOffer(normalized);
      } catch (err) {
        if (!isCancelled) {
          toastMessages?.(
            "error",
            "",
            err?.message || t("ErrorLoadingData") || "Error loading data."
          );
        }
      } finally {
        if (!isCancelled) ctx.setIsLoad(false);
      }
    };
    run();

    return () => {
      isCancelled = true;
    };
  }, [lineId, offer, svcOffers, ctx, t, toastMessages]);

  //==========================================
  // When the user switches to a different tab (selectedTab changes),
  // restore the snapshot that belongs to that target tab id.
  // This gives true isolation across multiple open quotes.
  //==================================
  useEffect(() => {
    if (!selectedTab || selectedTab === tabId) return; // nothing to do
    setTabId(selectedTab);

    // look up the new tab's snapshot and restore its state
    const snap = pickFromState(M_State, selectedTab);
    if (snap && Array.isArray(snap)) {
      const snapOffer = pickFromTabSnapshot(snap, "offer");
      const snapLineId = pickFromTabSnapshot(snap, "lineId");
      setOffer(snapOffer || null);
      setLineId(snapLineId || null);
    } else {
      // if there is no snapshot for the new tab, keep local states empty;
      // this avoids bleeding previous tab's data
      setOffer(null);
      setLineId(null);
    }
  }, [selectedTab, tabId, M_State]);

  //==========================================
  // Persist a small per-tab snapshot whenever key pieces change.
  // Debounced a bit so we do not hammer the store on every tiny change.
  // Snapshot shape per tab:
  //   [{ key:'offer', value: offer }, { key:'lineId', value: lineId }]
  //==================================
  const persistTimer = useRef(null);
  useEffect(() => {
    if (!tabId) return;

    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      const snap = [
        { key: "offer", value: offer },
        { key: "lineId", value: lineId },
      ];
      dispatch(M_StateActions.addM_State({ key: tabId, value: snap }));
    }, 200);

    return () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    };
  }, [tabId, offer, lineId, dispatch]);

  //==========================================
  // Save handler: POST the same payload back to RateOffers (mirror of search results behavior)
  //==================================
  const onSave = useCallback(
    async (payload) => {
      try {
        ctx.setIsLoad(true);
        // Prefer the original server payload if we have it
        await svcOffers.post(payload?.__raw || payload || {});
        toastMessages?.(
          "success",
          "",
          t("SavedSuccessfully") || "Saved successfully."
        );
      } catch (err) {
        toastMessages?.(
          "error",
          "",
          err?.message || t("ErrorSaving") || "Error saving offer."
        );
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [svcOffers, ctx, t, toastMessages]
  );

  return (
    <div style={{ marginTop: 12, padding: "20px" }}>
      {/* empty state while we do not have a payload */}
      {!offer ? (
        <div style={{ padding: 12, color: "gray", fontStyle: "italic" }}>
          {t("No results")}
        </div>
      ) : (
        // Reuse the exact same tile UI for one saved offer
        <div
          className="firstDivContainer"
          style={{
            background: "var(--color-white)",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              color: "var(--color-red-dark)",
              fontSize: "20px",
              display: "inline-block",
              marginBottom: "20px",
            }}
          >
            <span>{t("LabelViewInstantQuote") || "View Instant Quote"}</span>
          </div>
          {/* ResultsTile expects camelCase keys like providerName, originPortName, etc.
             After normalization above, everything lines up and renders. */}
          <ResultsTile
            offer={offer}
            index={0}
            t={t}
            onSave={onSave}
            viewMode={true}
          />
        </div>
      )}
    </div>
  );
}

InstantQuotesConfigCrud.propTypes = {
  toastMessages: PropTypes.func,
};
